import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  IconButton,
  Button,
  CircularProgress,
  makeStyles,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { Close, Add, Delete } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 340,
    maxWidth: 400,
    height: 500,
    borderRadius: 12,
    overflow: "hidden",
  },
  titleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 8px 6px 16px",
    minHeight: 40,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 4,
    padding: "4px 6px",
    overflowY: "auto",
    flex: 1,
    alignContent: "start",
  },
  stickerItem: {
    position: "relative",
    cursor: "pointer",
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "transparent",
    transition: "transform 0.12s, background-color 0.12s",
    aspectRatio: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 4,
    "&:hover": {
      transform: "scale(1.08)",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.05)",
    },
    "&:hover $deleteBtn": {
      opacity: 1,
    },
  },
  stickerImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },
  deleteBtn: {
    position: "absolute",
    top: 1,
    right: 1,
    padding: 2,
    backgroundColor: "rgba(0,0,0,0.55)",
    color: "#fff",
    opacity: 0,
    transition: "opacity 0.15s",
    "& svg": {
      fontSize: 11,
    },
    "&:hover": {
      backgroundColor: "rgba(220,0,0,0.85)",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.palette.text.secondary,
    gap: 12,
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    padding: "6px 12px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  uploadInput: {
    display: "none",
  },
  tabPanel: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
}));

const StickerPicker = ({ open, onClose, onStickerSend, ticketId }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchStickers = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/stickers");
      setStickers(data);
    } catch (err) {
      console.error("Erro ao carregar stickers:", err);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchStickers();
  }, [fetchStickers]);

  const sentStickers = stickers.filter((s) => s.source === "sent");
  const favStickers = stickers.filter((s) => s.source === "favorited");
  const currentList = tab === 0 ? sentStickers : favStickers;

  const handleStickerClick = async (sticker) => {
    if (sending) return;
    setSending(true);
    try {
      const response = await fetch(sticker.filePath);
      const blob = await response.blob();
      const file = new File([blob], `sticker_${Date.now()}.webp`, {
        type: "image/webp",
      });

      const formData = new FormData();
      formData.append("medias", file);
      formData.append("body", "");
      formData.append("fromMe", true);
      formData.append("isPrivate", "false");
      formData.append("isSticker", "true");
      formData.append("stickerLibraryId", String(sticker.id));

      await api.post(`/messages/${ticketId}`, formData);
      toast.success("Figurinha enviada!");
      onClose();
    } catch (err) {
      console.error("Erro ao enviar sticker:", err);
      toast.error("Erro ao enviar figurinha");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteSticker = async (e, stickerId) => {
    e.stopPropagation();
    try {
      await api.delete(`/stickers/${stickerId}`);
      setStickers((prev) => prev.filter((s) => s.id !== stickerId));
      toast.success("Figurinha removida!");
    } catch (err) {
      toast.error("Erro ao remover figurinha");
    }
  };

  const handleUploadNew = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = null;

    setSending(true);
    try {
      const isWebp =
        file.type === "image/webp" ||
        file.name.toLowerCase().endsWith(".webp");

      let stickerBlob;
      if (isWebp) {
        stickerBlob = file;
      } else {
        stickerBlob = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxSize = 512;
            let w = img.width,
              h = img.height;
            if (w > h) {
              h = Math.round((h * maxSize) / w);
              w = maxSize;
            } else {
              w = Math.round((w * maxSize) / h);
              h = maxSize;
            }
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob(
              (blob) =>
                blob ? resolve(blob) : reject(new Error("Falha na conversao")),
              "image/webp",
              0.8
            );
          };
          img.onerror = () => reject(new Error("Erro ao carregar imagem"));
          img.src = URL.createObjectURL(file);
        });
      }

      if (stickerBlob.size > 500000) {
        toast.warn("Imagem muito grande para sticker. Tente uma imagem menor.");
        setSending(false);
        return;
      }

      const stickerFile = new File(
        [stickerBlob],
        `sticker_${Date.now()}.webp`,
        { type: "image/webp" }
      );

      const formData = new FormData();
      formData.append("medias", stickerFile);
      formData.append("body", "");
      formData.append("fromMe", true);
      formData.append("isPrivate", "false");
      formData.append("isSticker", "true");

      await api.post(`/messages/${ticketId}`, formData);
      toast.success("Figurinha enviada!");

      setTimeout(() => fetchStickers(), 500);
    } catch (err) {
      console.error("Erro ao enviar sticker:", err);
      toast.error("Erro ao enviar figurinha");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="sm"
    >
      <div className={classes.titleBar}>
        <Typography variant="h6" style={{ fontSize: 16 }}>
          Figurinhas
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </div>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label={`Enviadas (${sentStickers.length})`} />
        <Tab label={`Favoritas (${favStickers.length})`} />
      </Tabs>

      <div className={classes.tabPanel}>
        {loading ? (
          <div className={classes.emptyState}>
            <CircularProgress size={32} />
          </div>
        ) : currentList.length === 0 ? (
          <div className={classes.emptyState}>
            <span style={{ fontSize: 48 }}>&#x1F3F7;&#xFE0F;</span>
            <Typography variant="body2">
              {tab === 0
                ? "Nenhuma figurinha enviada ainda"
                : "Nenhuma figurinha favoritada"}
            </Typography>
            {tab === 0 && (
              <Typography variant="caption">
                Envie uma figurinha e ela aparece aqui automaticamente
              </Typography>
            )}
          </div>
        ) : (
          <div className={classes.grid}>
            {currentList.map((sticker) => (
              <div
                key={sticker.id}
                className={classes.stickerItem}
                onClick={() => handleStickerClick(sticker)}
              >
                <img
                  src={sticker.filePath}
                  alt={sticker.name || "sticker"}
                  className={classes.stickerImg}
                  loading="lazy"
                />
                <Tooltip title="Remover">
                  <IconButton
                    className={classes.deleteBtn}
                    size="small"
                    onClick={(e) => handleDeleteSticker(e, sticker.id)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <input
          type="file"
          id="upload-new-sticker"
          accept="image/*,.webp"
          className={classes.uploadInput}
          onChange={handleUploadNew}
        />
        <label htmlFor="upload-new-sticker">
          <Button
            variant="outlined"
            color="primary"
            component="span"
            startIcon={sending ? <CircularProgress size={16} /> : <Add />}
            disabled={sending}
          >
            Enviar nova figurinha
          </Button>
        </label>
      </div>
    </Dialog>
  );
};

export default StickerPicker;
